import React from 'react';

function clampPct(v: number | null | undefined) {
    if (v == null || !Number.isFinite(v)) return 0;
    return Math.max(0, Math.min(100, v));
}

function fmt1(x: number | null | undefined) {
    if (x == null || !Number.isFinite(x)) return '—';
    return x.toFixed(1);
}

function spiTone(spi: number | null | undefined) {
    if (spi == null || !Number.isFinite(spi)) {
        return {
            badge: 'bg-gray-100 text-gray-700',
            label: 'No data',
        };
    }
    if (spi >= 1) {
        return {
            badge: 'bg-emerald-100 text-emerald-700',
            label: 'On track',
        };
    }
    if (spi >= 0.9) {
        return {
            badge: 'bg-amber-100 text-amber-700',
            label: 'Watch',
        };
    }
    return {
        badge: 'bg-rose-100 text-rose-700',
        label: 'Delayed',
    };
}

export default function ProgressHealthChartCard({
    spi,
    evPct,
    pvPct,
}: {
    spi: number | null;
    evPct: number | null;
    pvPct: number | null;
}) {
    const ev = clampPct(evPct);
    const pv = clampPct(pvPct);
    const gap = evPct != null && pvPct != null ? evPct - pvPct : null;
    const tone = spiTone(spi);

    return (
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-gray-900">Progress Health</div>
                    <div className="mt-1 text-[11px] text-gray-500">계획 대비 실적 진도 시각화</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tone.badge}`}>{tone.label}</span>
            </div>

            <div className="space-y-4">
                <div>
                    <div className="mb-1.5 flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-700">Planned Progress</span>
                        <span className="tabular-nums text-gray-900">{fmt1(pvPct)}%</span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-slate-400" style={{ width: `${pv}%` }} />
                    </div>
                </div>

                <div>
                    <div className="mb-1.5 flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-700">Earned Progress</span>
                        <span className="tabular-nums text-gray-900">{fmt1(evPct)}%</span>
                    </div>
                    <div className="h-4 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-blue-600" style={{ width: `${ev}%` }} />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                        <div className="text-[11px] text-gray-500">SPI</div>
                        <div className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                            {spi == null || !Number.isFinite(spi) ? '—' : spi.toFixed(2)}
                        </div>
                    </div>

                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                        <div className="text-[11px] text-gray-500">Gap</div>
                        <div
                            className={`mt-1 text-lg font-semibold tabular-nums ${
                                gap == null ? 'text-gray-900' : gap >= 0 ? 'text-emerald-700' : 'text-rose-700'
                            }`}
                        >
                            {gap == null ? '—' : `${gap >= 0 ? '+' : ''}${fmt1(gap)}pp`}
                        </div>
                    </div>

                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                        <div className="text-[11px] text-gray-500">Signal</div>
                        <div className="mt-1 text-lg font-semibold text-gray-900">{tone.label}</div>
                    </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    {gap == null
                        ? '계획/실적 비교 데이터가 없습니다.'
                        : gap >= 0
                          ? `실적이 계획보다 ${fmt1(Math.abs(gap))}pp 앞서 있습니다.`
                          : `실적이 계획보다 ${fmt1(Math.abs(gap))}pp 뒤처져 있습니다.`}
                </div>
            </div>
        </div>
    );
}
